/**
 * Entity Factory for Kobayashi Maru
 * Creates pre-configured entities with appropriate components
 * 
 * NOTE: Enemy ship creation now uses configuration-driven templates.
 * See entityTemplates.ts for enemy definitions and genericFactory.ts for the
 * createEnemy() function. The individual createXXXShip functions below are
 * thin wrappers maintained for backwards compatibility.
 */
import { addEntity, addComponent } from 'bitecs';
import { Position, Velocity, Faction, SpriteRef, CompositeSpriteRef, Health, Shield, Turret, Target, Projectile, Collider, WeaponProperties, TurretUpgrade, Rotation } from './components';
import { FactionId, TurretType, TURRET_CONFIG, ProjectileType, PROJECTILE_CONFIG, GAME_CONFIG } from '../types/constants';
import type { GameWorld } from './world';
import { incrementEntityCount } from './world';
import { createEnemy, createEnemyFromTemplate, createEnemies } from './genericFactory';
import { ENEMY_TEMPLATES, getEnemyTemplate, getEnemyFactionIds, TURRET_TEMPLATES, getTurretTemplate, PROJECTILE_TEMPLATES, getProjectileTemplate } from './entityTemplates';
import { resetProjectile } from './entityReset';
import { PoolManager } from './PoolManager';

const USE_POOLING = true;

// Re-export generic factory and templates for convenient access
export { createEnemy, createEnemyFromTemplate, createEnemies };
export { ENEMY_TEMPLATES, getEnemyTemplate, getEnemyFactionIds };
export { TURRET_TEMPLATES, getTurretTemplate, PROJECTILE_TEMPLATES, getProjectileTemplate };

// Placeholder sprite index - will be replaced when sprite system is implemented
const PLACEHOLDER_SPRITE_INDEX = 0;

/**
 * Creates the Kobayashi Maru entity (center objective)
 * @param world - The ECS world
 * @param x - X position
 * @param y - Y position
 * @returns Entity ID
 */
export function createKobayashiMaru(world: GameWorld, x: number, y: number): number {
  const eid = addEntity(world);

  addComponent(world, eid, Position);
  Position.x[eid] = x;
  Position.y[eid] = y;

  addComponent(world, eid, Velocity);
  Velocity.x[eid] = 0;
  Velocity.y[eid] = 0;

  addComponent(world, eid, Faction);
  Faction.id[eid] = FactionId.FEDERATION;

  addComponent(world, eid, SpriteRef);
  SpriteRef.index[eid] = PLACEHOLDER_SPRITE_INDEX;

  addComponent(world, eid, Rotation);
  Rotation.angle[eid] = 0;

  addComponent(world, eid, Health);
  Health.current[eid] = 500;
  Health.max[eid] = 500;

  addComponent(world, eid, Shield);
  Shield.current[eid] = 200;
  Shield.max[eid] = 200;

  // Add basic defense weapon to Kobayashi Maru
  addComponent(world, eid, Turret);
  Turret.range[eid] = GAME_CONFIG.KOBAYASHI_MARU_DEFENSE_RANGE;
  Turret.fireRate[eid] = GAME_CONFIG.KOBAYASHI_MARU_DEFENSE_FIRE_RATE;
  Turret.damage[eid] = GAME_CONFIG.KOBAYASHI_MARU_DEFENSE_DAMAGE;
  Turret.lastFired[eid] = 0;
  Turret.turretType[eid] = TurretType.DISRUPTOR_BANK; // Use disruptor type for main ship

  addComponent(world, eid, Target);
  Target.entityId[eid] = 0;
  Target.hasTarget[eid] = 0;
  Target.entityId2[eid] = 0;
  Target.hasTarget2[eid] = 0;
  Target.entityId3[eid] = 0;
  Target.hasTarget3[eid] = 0;

  incrementEntityCount();
  return eid;
}

/**
 * Creates a turret entity
 * @param world - The ECS world
 * @param x - X position
 * @param y - Y position
 * @param turretType - Type of turret to create
 * @returns Entity ID
 */
export function createTurret(world: GameWorld, x: number, y: number, turretType: number): number {
  const eid = addEntity(world);

  addComponent(world, eid, Position);
  Position.x[eid] = x;
  Position.y[eid] = y;

  addComponent(world, eid, Velocity);
  Velocity.x[eid] = 0;
  Velocity.y[eid] = 0;

  addComponent(world, eid, Faction);
  Faction.id[eid] = FactionId.FEDERATION;

  addComponent(world, eid, CompositeSpriteRef);
  CompositeSpriteRef.baseIndex[eid] = PLACEHOLDER_SPRITE_INDEX;
  CompositeSpriteRef.barrelIndex[eid] = PLACEHOLDER_SPRITE_INDEX;

  addComponent(world, eid, Rotation);
  Rotation.angle[eid] = 0;

  // Get turret stats based on type
  const config = TURRET_CONFIG[turretType] || TURRET_CONFIG[TurretType.PHASER_ARRAY];

  addComponent(world, eid, Health);
  Health.current[eid] = config.health;
  Health.max[eid] = config.health;

  addComponent(world, eid, Shield);
  Shield.current[eid] = config.shield;
  Shield.max[eid] = config.shield;

  addComponent(world, eid, Turret);
  Turret.range[eid] = config.range;
  Turret.fireRate[eid] = config.fireRate;
  Turret.damage[eid] = config.damage;
  Turret.lastFired[eid] = 0;
  Turret.turretType[eid] = turretType;

  addComponent(world, eid, Target);
  Target.entityId[eid] = 0;
  Target.hasTarget[eid] = 0;
  Target.entityId2[eid] = 0;
  Target.hasTarget2[eid] = 0;
  Target.entityId3[eid] = 0;
  Target.hasTarget3[eid] = 0;

  // Add upgrade component to all turrets (starts at level 0)
  addComponent(world, eid, TurretUpgrade);
  TurretUpgrade.damageLevel[eid] = 0;
  TurretUpgrade.rangeLevel[eid] = 0;
  TurretUpgrade.fireRateLevel[eid] = 0;
  TurretUpgrade.multiTargetLevel[eid] = 0;
  TurretUpgrade.specialLevel[eid] = 0;

  // Add weapon properties for special turrets
  if (turretType === TurretType.TETRYON_BEAM) {
    // Tetryon: 3x shield damage, 0.5x hull damage
    addComponent(world, eid, WeaponProperties);
    WeaponProperties.shieldDamageMultiplier[eid] = 3.0;
    WeaponProperties.hullDamageMultiplier[eid] = 0.5;
    WeaponProperties.critChance[eid] = 0;
    WeaponProperties.critMultiplier[eid] = 1.0;
    WeaponProperties.aoeRadius[eid] = 0;
    WeaponProperties.statusEffectType[eid] = 0; // No status effect
    WeaponProperties.statusEffectChance[eid] = 0;
  } else if (turretType === TurretType.PLASMA_CANNON) {
    // Plasma: Applies burning (4 dmg/sec for 5 seconds)
    addComponent(world, eid, WeaponProperties);
    WeaponProperties.shieldDamageMultiplier[eid] = 1.0;
    WeaponProperties.hullDamageMultiplier[eid] = 1.0;
    WeaponProperties.critChance[eid] = 0;
    WeaponProperties.critMultiplier[eid] = 1.0;
    WeaponProperties.aoeRadius[eid] = 0;
    WeaponProperties.statusEffectType[eid] = 1; // Burn
    WeaponProperties.statusEffectChance[eid] = 1.0; // 100% chance
  } else if (turretType === TurretType.POLARON_BEAM) {
    // Polaron: Stacking drain effect
    addComponent(world, eid, WeaponProperties);
    WeaponProperties.shieldDamageMultiplier[eid] = 1.0;
    WeaponProperties.hullDamageMultiplier[eid] = 1.0;
    WeaponProperties.critChance[eid] = 0;
    WeaponProperties.critMultiplier[eid] = 1.0;
    WeaponProperties.aoeRadius[eid] = 0;
    WeaponProperties.statusEffectType[eid] = 3; // Drain
    WeaponProperties.statusEffectChance[eid] = 1.0; // 100% chance
  } else if (turretType === TurretType.PHASER_ARRAY) {
    // Phaser: 5% chance to disable enemy weapons
    addComponent(world, eid, WeaponProperties);
    WeaponProperties.shieldDamageMultiplier[eid] = 1.0;
    WeaponProperties.hullDamageMultiplier[eid] = 1.0;
    WeaponProperties.critChance[eid] = 0;
    WeaponProperties.critMultiplier[eid] = 1.0;
    WeaponProperties.aoeRadius[eid] = 0;
    WeaponProperties.statusEffectType[eid] = 4; // Disable
    WeaponProperties.statusEffectChance[eid] = 0.05; // 5% chance
  } else if (turretType === TurretType.DISRUPTOR_BANK) {
    // Disruptor: 15% chance to slow enemies
    addComponent(world, eid, WeaponProperties);
    WeaponProperties.shieldDamageMultiplier[eid] = 1.0;
    WeaponProperties.hullDamageMultiplier[eid] = 1.0;
    WeaponProperties.critChance[eid] = 0;
    WeaponProperties.critMultiplier[eid] = 1.0;
    WeaponProperties.aoeRadius[eid] = 0;
    WeaponProperties.statusEffectType[eid] = 2; // Slow
    WeaponProperties.statusEffectChance[eid] = 0.15; // 15% chance
  }

  incrementEntityCount();
  return eid;
}

/**
 * Creates a projectile entity
 * @param world - The ECS world
 * @param x - Starting X position
 * @param y - Starting Y position
 * @param targetX - Target X position
 * @param targetY - Target Y position
 * @param damage - Damage to deal on impact
 * @param projectileType - Type of projectile
 * @param targetEntityId - Optional target entity ID for homing
 * @returns Entity ID
 */
export function createProjectile(
  world: GameWorld,
  x: number,
  y: number,
  targetX: number,
  targetY: number,
  damage: number,
  projectileType: number,
  targetEntityId: number = 0
): number {
  const eid = USE_POOLING
    ? PoolManager.getInstance().acquireProjectile()
    : addEntity(world);

  if (USE_POOLING) {
    resetProjectileComponents(eid);
  }

  addComponent(world, eid, Position);
  Position.x[eid] = x;
  Position.y[eid] = y;

  addComponent(world, eid, Velocity);
  // Velocity will be set by the projectile system or caller
  Velocity.x[eid] = 0;
  Velocity.y[eid] = 0;

  addComponent(world, eid, Projectile);
  Projectile.damage[eid] = damage;
  Projectile.projectileType[eid] = projectileType;
  Projectile.targetEntityId[eid] = targetEntityId;

  // Set config based on type
  const config = PROJECTILE_CONFIG[projectileType] || PROJECTILE_CONFIG[ProjectileType.PHOTON_TORPEDO];
  Projectile.speed[eid] = config.speed;
  Projectile.lifetime[eid] = config.lifetime;

  // Calculate initial velocity
  const dx = targetX - x;
  const dy = targetY - y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  // Default rotation and velocity
  let angle = 0;
  if (dist > 0) {
    Velocity.x[eid] = (dx / dist) * config.speed;
    Velocity.y[eid] = (dy / dist) * config.speed;
    angle = Math.atan2(dy, dx);
  }

  addComponent(world, eid, Rotation);
  // Add 90 degrees (HALF_PI) because textures point up by default
  Rotation.angle[eid] = angle + Math.PI / 2;

  addComponent(world, eid, Faction);
  Faction.id[eid] = FactionId.PROJECTILE; // Projectiles belong to Federation for now

  addComponent(world, eid, SpriteRef);
  SpriteRef.index[eid] = PLACEHOLDER_SPRITE_INDEX; // Will be handled by sprite manager

  addComponent(world, eid, Collider);
  Collider.radius[eid] = config.size;
  Collider.layer[eid] = 2; // Projectile layer (need to define layers properly later)
  Collider.mask[eid] = 1;  // Collides with enemies (layer 1)

  if (!USE_POOLING) incrementEntityCount();
  return eid;
}

/**
 * Reset projectile components
 * @deprecated Use resetProjectile from entityReset.ts instead
 * TODO: Remove in future cleanup - this is a wrapper for backwards compatibility
 */
function resetProjectileComponents(eid: number): void {
  resetProjectile(eid);
}

/**
 * Creates an enemy projectile entity (fired by enemies toward Kobayashi Maru/turrets)
 * @param world - The ECS world
 * @param x - Starting X position
 * @param y - Starting Y position
 * @param targetX - Target X position
 * @param targetY - Target Y position
 * @param damage - Damage to deal on impact
 * @param projectileType - Type of projectile
 * @returns Entity ID
 */
export function createEnemyProjectile(
  world: GameWorld,
  x: number,
  y: number,
  targetX: number,
  targetY: number,
  damage: number,
  projectileType: number
): number {
  const eid = USE_POOLING
    ? PoolManager.getInstance().acquireProjectile()
    : addEntity(world);

  if (USE_POOLING) {
    resetProjectileComponents(eid);
  }

  addComponent(world, eid, Position);
  Position.x[eid] = x;
  Position.y[eid] = y;

  addComponent(world, eid, Velocity);

  addComponent(world, eid, Projectile);
  Projectile.damage[eid] = damage;
  Projectile.projectileType[eid] = projectileType;
  Projectile.targetEntityId[eid] = 0; // No homing for enemy projectiles

  // Set config based on type
  const config = PROJECTILE_CONFIG[projectileType] || PROJECTILE_CONFIG[ProjectileType.DISRUPTOR_BOLT];
  Projectile.speed[eid] = config.speed;
  Projectile.lifetime[eid] = config.lifetime;

  // Calculate initial velocity toward target
  const dx = targetX - x;
  const dy = targetY - y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  let angle = 0;
  if (dist > 0) {
    Velocity.x[eid] = (dx / dist) * config.speed;
    Velocity.y[eid] = (dy / dist) * config.speed;
    angle = Math.atan2(dy, dx);
  }

  addComponent(world, eid, Rotation);
  // Add 90 degrees because textures point up
  Rotation.angle[eid] = angle + Math.PI / 2;

  addComponent(world, eid, Faction);
  Faction.id[eid] = FactionId.ENEMY_PROJECTILE; // Enemy projectile faction

  addComponent(world, eid, SpriteRef);
  SpriteRef.index[eid] = PLACEHOLDER_SPRITE_INDEX;

  addComponent(world, eid, Collider);
  Collider.radius[eid] = config.size;
  Collider.layer[eid] = 3; // Enemy projectile layer
  Collider.mask[eid] = 0;  // Federation entities (layer 0)

  if (!USE_POOLING) incrementEntityCount();
  return eid;
}
